import React from 'react';
import { formatNumber, getBollingerBandIcon, getFibonacciIcon, TickerTechnicalData } from './technicalsTabUtils';
import { GoldenCrossIcon, DeathCrossIcon } from '../components/Icons';

// Date columns for technicals table
export const TECHNICALS_DATE_COLUMNS = [
  { daysAgo: 0, label: 'T' },
  { daysAgo: 1, label: 'T-1' },
  { daysAgo: 2, label: 'T-2' },
  { daysAgo: 3, label: 'T-3' },
  { daysAgo: 4, label: 'T-4' },
  { daysAgo: 5, label: 'T-5' },
  { daysAgo: 6, label: 'T-6' },
  { daysAgo: 7, label: 'T-7' },
  { daysAgo: 14, label: 'T-14' },
  { daysAgo: 30, label: 'T-30' },
  { daysAgo: 60, label: 'T-60' },
  { daysAgo: 90, label: 'T-90' },
];

// Interface for API response indicator format
export interface IndicatorData {
  indicatorKey: string;
  indicatorValue: number | null;
  indicatorNonNumericValue: string | null;
  daysAgo: number;
  date: string;
  ticker_basics_id?: number;
}

// Interface for ticker technical data from API
export interface TickerTechnicalDataFromAPI {
  ticker_symbol: string;
  ticker_basics_id: number;
  indicators: IndicatorData[];
}

// Interface for daily price data
export interface DailyPriceData {
  date: string;
  close_price_adjusted: number;
}

// Interface for indicator accessor functions
export interface IndicatorAccessors {
  getIndicatorValue: (indicatorKey: string, daysAgo: number) => number | null;
  getIndicatorNonNumericValue: (indicatorKey: string, daysAgo: number) => string | null;
}

/**
 * Create indicator accessor functions from API response data
 */
export const createIndicatorAccessors = (
  tickerData: TickerTechnicalDataFromAPI | null
): IndicatorAccessors => {
  const getIndicatorValue = (indicatorKey: string, daysAgo: number): number | null => {
    if (!tickerData) return null;
    
    const indicator = tickerData.indicators.find(
      (ind) => ind.indicatorKey === indicatorKey && ind.daysAgo === daysAgo
    );
    
    return indicator ? indicator.indicatorValue : null;
  };

  const getIndicatorNonNumericValue = (indicatorKey: string, daysAgo: number): string | null => {
    if (!tickerData) return null;
    
    const indicator = tickerData.indicators.find(
      (ind) => ind.indicatorKey === indicatorKey && ind.daysAgo === daysAgo
    );
    
    if (indicator && indicator.indicatorNonNumericValue !== null && indicator.indicatorNonNumericValue !== undefined) {
      const value = indicator.indicatorNonNumericValue.trim();
      return value !== '' ? indicator.indicatorNonNumericValue : null;
    }
    
    return null;
  };

  return { getIndicatorValue, getIndicatorNonNumericValue };
};

/**
 * Detect golden cross for a given indicator pair
 */
export const hasGoldenCross = (
  accessors: IndicatorAccessors,
  currentIndicator: string,
  compareIndicator: string
): boolean => {
  // Get current values (daysAgo = 0)
  const currentValue = accessors.getIndicatorValue(currentIndicator, 0);
  const currentCompareValue = accessors.getIndicatorValue(compareIndicator, 0);

  if (currentValue === null || currentValue === undefined || 
      currentCompareValue === null || currentCompareValue === undefined) {
    return false;
  }

  // Check if current value > compare value (golden cross condition)
  if (currentValue <= currentCompareValue) {
    return false;
  }

  // Check if there was a cross in the historical data (daysAgo 1-6)
  for (let daysAgo = 1; daysAgo <= 6; daysAgo++) {
    const historicalValue = accessors.getIndicatorValue(currentIndicator, daysAgo);
    const historicalCompareValue = accessors.getIndicatorValue(compareIndicator, daysAgo);
    
    if (historicalValue !== null && historicalValue !== undefined && 
        historicalCompareValue !== null && historicalCompareValue !== undefined) {
      // Check if historical value was <= historical compare value (indicating a cross occurred)
      if (historicalValue <= historicalCompareValue) {
        return true;
      }
    }
  }

  return false;
};

/**
 * Detect death cross for a given indicator pair
 */
export const hasDeathCross = (
  accessors: IndicatorAccessors,
  currentIndicator: string,
  compareIndicator: string
): boolean => {
  // Get current values (daysAgo = 0)
  const currentValue = accessors.getIndicatorValue(currentIndicator, 0);
  const currentCompareValue = accessors.getIndicatorValue(compareIndicator, 0);

  if (currentValue === null || currentValue === undefined || 
      currentCompareValue === null || currentCompareValue === undefined) {
    return false;
  }

  // Check if current value < compare value (death cross condition)
  if (currentValue >= currentCompareValue) {
    return false;
  }

  // Check if there was a cross in the historical data (daysAgo 1-6)
  for (let daysAgo = 1; daysAgo <= 6; daysAgo++) {
    const historicalValue = accessors.getIndicatorValue(currentIndicator, daysAgo);
    const historicalCompareValue = accessors.getIndicatorValue(compareIndicator, daysAgo);
    
    if (historicalValue !== null && historicalValue !== undefined && 
        historicalCompareValue !== null && historicalCompareValue !== undefined) {
      // Check if historical value was >= historical compare value (indicating a cross occurred from above to below)
      if (historicalValue >= historicalCompareValue) {
        return true;
      }
    }
  }

  return false;
};

/**
 * Get value color for technical indicators (works with API response format)
 */
export const getTechnicalsValueColor = (
  accessors: IndicatorAccessors,
  dailyPrices: { [daysAgo: number]: DailyPriceData },
  indicatorKey: string,
  daysAgo: number
): string => {
  const high52w = accessors.getIndicatorValue('52W_HIGH', daysAgo);
  const low52w = accessors.getIndicatorValue('52W_LOW', daysAgo);
  const currentPrice = dailyPrices[daysAgo]?.close_price_adjusted;
  
  if (!high52w || !low52w || !currentPrice || high52w <= 0 || low52w <= 0) {
    return 'text-text-light-neutral dark:text-text-secondary';
  }
  
  const pctOffHigh = ((high52w - currentPrice) / high52w) * 100;
  const pctAboveLow = ((currentPrice - low52w) / low52w) * 100;
  
  // For Last Closing Price: Compare with previous closing price
  if (indicatorKey === 'last_closing_price') {
    const currentPrice = dailyPrices[daysAgo]?.close_price_adjusted;
    const previousPrice = dailyPrices[daysAgo + 1]?.close_price_adjusted;
    
    if (currentPrice !== undefined && currentPrice !== null && 
        previousPrice !== undefined && previousPrice !== null) {
      if (currentPrice > previousPrice) {
        return 'text-green-light dark:text-green-500';
      } else if (currentPrice < previousPrice) {
        return 'text-red-light dark:text-red-500';
      } else {
        return 'text-text-light-neutral dark:text-text-secondary';
      }
    }
  }
  
  // For % Below 52-Week High: Green if close to high, red if close to low
  if (indicatorKey === 'pct_off_52w_high') {
    if (pctOffHigh <= 10) {
      return 'text-green-light dark:text-green-500';
    }
    if (pctAboveLow <= 10) {
      return 'text-red-light dark:text-red-500';
    }
  }
  
  // For % Above 52-Week Low: Only red if 10% or less
  if (indicatorKey === 'pct_above_52w_low') {
    if (pctAboveLow <= 10) {
      return 'text-red-light dark:text-red-500';
    }
  }
  
  // VMA color logic
  if (indicatorKey.startsWith('VMA_')) {
    const currentVMA = accessors.getIndicatorValue(indicatorKey, daysAgo);
    if (currentVMA === undefined || currentVMA === null) {
      return 'text-text-light-neutral dark:text-text-secondary';
    }
    
    const vmaPairs: { [key: string]: string } = {
      'VMA_5': 'VMA_10',
      'VMA_10': 'VMA_20',
      'VMA_20': 'VMA_50',
      'VMA_50': 'VMA_100',
      'VMA_100': 'VMA_200'
    };
    
    const vmaCompareKey = vmaPairs[indicatorKey];
    if (vmaCompareKey) {
      const compareVMA = accessors.getIndicatorValue(vmaCompareKey, daysAgo);
      if (compareVMA !== undefined && compareVMA !== null) {
        const smaKey = indicatorKey.replace('VMA_', 'SMA_');
        const smaCompareKey = vmaCompareKey.replace('VMA_', 'SMA_');
        const currentSMA = accessors.getIndicatorValue(smaKey, daysAgo);
        const compareSMA = accessors.getIndicatorValue(smaCompareKey, daysAgo);
        
        if (currentSMA !== undefined && currentSMA !== null && 
            compareSMA !== undefined && compareSMA !== null) {
          const vmaCondition = currentVMA >= compareVMA;
          const smaCondition = currentSMA >= compareSMA;
          if (vmaCondition && smaCondition) {
            return 'text-green-light dark:text-green-500';
          } else if (vmaCondition && !smaCondition) {
            return 'text-red-light dark:text-red-500';
          }
        }
      }
    }
  }
  
  // EMA color logic
  if (indicatorKey.startsWith('EMA_')) {
    const currentEMA = accessors.getIndicatorValue(indicatorKey, daysAgo);
    if (currentEMA === undefined || currentEMA === null || indicatorKey === 'EMA_200') {
      return 'text-text-light-neutral dark:text-text-secondary';
    }
    
    const emaPairs: { [key: string]: string } = {
      'EMA_9': 'EMA_12',
      'EMA_12': 'EMA_21',
      'EMA_21': 'EMA_26',
      'EMA_26': 'EMA_50',
      'EMA_50': 'EMA_200'
    };
    
    const compareKey = emaPairs[indicatorKey];
    if (compareKey) {
      const compareEMA = accessors.getIndicatorValue(compareKey, daysAgo);
      if (compareEMA !== undefined && compareEMA !== null) {
        return currentEMA >= compareEMA ? 'text-green-light dark:text-green-500' : 'text-red-light dark:text-red-500';
      }
    }
  }
  
  // SMA color logic
  if (indicatorKey.startsWith('SMA_')) {
    const currentSMA = accessors.getIndicatorValue(indicatorKey, daysAgo);
    if (currentSMA === undefined || currentSMA === null || indicatorKey === 'SMA_W200') {
      return 'text-text-light-neutral dark:text-text-secondary';
    }
    
    const smaPairs: { [key: string]: string } = {
      'SMA_5': 'SMA_10',
      'SMA_10': 'SMA_20',
      'SMA_20': 'SMA_50',
      'SMA_50': 'SMA_100',
      'SMA_100': 'SMA_200',
      'SMA_200': 'SMA_W50',
      'SMA_W50': 'SMA_W200'
    };
    
    const compareKey = smaPairs[indicatorKey];
    if (compareKey) {
      const compareSMA = accessors.getIndicatorValue(compareKey, daysAgo);
      if (compareSMA !== undefined && compareSMA !== null) {
        return currentSMA >= compareSMA ? 'text-green-light dark:text-green-500' : 'text-red-light dark:text-red-500';
      }
    }
  }
  
  // RSI color logic
  if (indicatorKey.startsWith('RSI_')) {
    const currentRSI = accessors.getIndicatorValue(indicatorKey, daysAgo);
    if (currentRSI === undefined || currentRSI === null) {
      return 'text-text-light-neutral dark:text-text-secondary';
    }
    
    const rsiPairs: { [key: string]: string } = {
      'RSI_9': 'RSI_14',
      'RSI_14': 'RSI_21'
    };
    
    const compareKey = rsiPairs[indicatorKey];
    if (compareKey) {
      const compareRSI = accessors.getIndicatorValue(compareKey, daysAgo);
      if (compareRSI !== undefined && compareRSI !== null) {
        return currentRSI >= compareRSI ? 'text-green-light dark:text-green-500' : 'text-red-light dark:text-red-500';
      }
    }
  }
  
  // MACD color logic
  if (indicatorKey === 'MACD_LINE' || indicatorKey === 'MACD_SIGNAL' || indicatorKey === 'MACD_HIST') {
    const macdValue = accessors.getIndicatorValue(indicatorKey, daysAgo);
    if (macdValue === undefined || macdValue === null) {
      return 'text-text-light-neutral dark:text-text-secondary';
    }
    if (macdValue > 0) {
      return 'text-green-light dark:text-green-500';
    } else if (macdValue < 0) {
      return 'text-red-light dark:text-red-500';
    }
  }
  
  // Stochastic Oscillator color logic
  if (indicatorKey.startsWith('STOCH_') && indicatorKey.endsWith('_K')) {
    const currentK = accessors.getIndicatorValue(indicatorKey, daysAgo);
    if (currentK === undefined || currentK === null) {
      return 'text-text-light-neutral dark:text-text-secondary';
    }
    const dKey = indicatorKey.replace('_K', '_D');
    const currentD = accessors.getIndicatorValue(dKey, daysAgo);
    if (currentD !== undefined && currentD !== null) {
      if (currentK > currentD) {
        return 'text-green-light dark:text-green-500';
      } else if (currentK < currentD) {
        return 'text-red-light dark:text-red-500';
      }
    }
  }
  
  if (indicatorKey.startsWith('STOCH_') && indicatorKey.endsWith('_D')) {
    const currentD = accessors.getIndicatorValue(indicatorKey, daysAgo);
    if (currentD === undefined || currentD === null) {
      return 'text-text-light-neutral dark:text-text-secondary';
    }
    const dPairs: { [key: string]: string } = {
      'STOCH_9_D': 'STOCH_14_D',
      'STOCH_14_D': 'STOCH_21_D'
    };
    const compareKey = dPairs[indicatorKey];
    if (compareKey) {
      const compareD = accessors.getIndicatorValue(compareKey, daysAgo);
      if (compareD !== undefined && compareD !== null) {
        if (currentD > compareD) {
          return 'text-green-light dark:text-green-500';
        } else if (currentD < compareD) {
          return 'text-red-light dark:text-red-500';
        }
      }
    }
  }
  
  // Bollinger Bands color logic
  if (indicatorKey.startsWith('BB_')) {
    const currentPrice = dailyPrices[daysAgo]?.close_price_adjusted;
    const upperBB = accessors.getIndicatorValue('BB_20_UPPER_20', daysAgo);
    const lowerBB = accessors.getIndicatorValue('BB_20_LOWER_20', daysAgo);
    if (currentPrice !== undefined && currentPrice !== null) {
      if (indicatorKey === 'BB_20_UPPER_20' && upperBB !== undefined && upperBB !== null) {
        if (currentPrice > upperBB) {
          return 'text-green-light dark:text-green-500';
        }
      }
      if (indicatorKey === 'BB_20_LOWER_20' && lowerBB !== undefined && lowerBB !== null) {
        if (currentPrice < lowerBB) {
          return 'text-red-light dark:text-red-500';
        }
      }
    }
  }
  
  // Fibonacci Swing Percentage color logic
  if (indicatorKey === 'FIB_SWING_PERCENTAGE') {
    const swingPercentage = accessors.getIndicatorValue(indicatorKey, daysAgo);
    if (swingPercentage !== undefined && swingPercentage !== null) {
      return swingPercentage >= 0 ? 'text-green-light dark:text-green-500' : 'text-red-light dark:text-red-500';
    }
  }
  
  return 'text-text-light-neutral dark:text-text-secondary';
};

/**
 * Build TickerTechnicalData structure for icon functions from API response
 */
export const buildTickerTechnicalDataForIcons = (
  tickerData: TickerTechnicalDataFromAPI | null,
  dailyPrices: { [daysAgo: number]: DailyPriceData },
  tickerSymbol: string,
  daysAgo: number
): TickerTechnicalData[string] | null => {
  if (!tickerData) return null;
  
  const technicalsMap: { [key: string]: any } = {};
  const currentPriceData = dailyPrices[daysAgo];
  
  // Build technicals map for current daysAgo
  tickerData.indicators.forEach((indicator) => {
    if (indicator.daysAgo === daysAgo) {
      technicalsMap[indicator.indicatorKey] = {
        ticker_basics_id: indicator.ticker_basics_id || tickerData.ticker_basics_id,
        indicator_key: indicator.indicatorKey,
        value: indicator.indicatorValue,
        nonNumericValue: indicator.indicatorNonNumericValue || null,
        date: indicator.date,
      };
    }
  });
  
  return {
    technicals: technicalsMap,
    price: currentPriceData ? {
      ticker_symbol: tickerSymbol,
      ticker_basics_id: tickerData.ticker_basics_id,
      close_price_adjusted: currentPriceData.close_price_adjusted,
      date: new Date(currentPriceData.date),
      previous_close_price_adjusted: dailyPrices[daysAgo + 1]?.close_price_adjusted || null,
      previous_date: dailyPrices[daysAgo + 1] ? new Date(dailyPrices[daysAgo + 1].date) : null,
    } : null,
    movingAverages: [],
  };
};

/**
 * Get cross icon for SMA/EMA indicators
 */
export const getCrossIcon = (
  accessors: IndicatorAccessors,
  indicatorKey: string,
  daysAgo: number
): { goldenCross: React.ReactNode; deathCross: React.ReactNode } => {
  let goldenCrossIcon: React.ReactNode = null;
  let deathCrossIcon: React.ReactNode = null;
  
  // Only show cross icons for current day (daysAgo === 0)
  if (daysAgo === 0) {
    // Check for golden cross and death cross for specific indicator pairs
    if (indicatorKey === 'SMA_20') {
      if (hasGoldenCross(accessors, 'SMA_20', 'SMA_50')) {
        goldenCrossIcon = (
          <span className="ml-1 group/golden-cross relative">
            <GoldenCrossIcon className="w-4 h-4" />
            <div className="absolute hidden group-hover/golden-cross:block left-0 top-full mt-1 w-48 bg-white dark:bg-background-card border border-gray-200 dark:border-border-color rounded-md shadow-lg p-2 text-xs text-gray-600 dark:text-text-secondary z-[60] font-sans whitespace-normal break-words">
              20-Day SMA crossed over 50-Day SMA in the past 5 days
            </div>
          </span>
        );
      } else if (hasDeathCross(accessors, 'SMA_20', 'SMA_50')) {
        deathCrossIcon = (
          <span className="ml-1 group/death-cross relative">
            <DeathCrossIcon className="w-4 h-4" />
            <div className="absolute hidden group-hover/death-cross:block left-0 top-full mt-1 w-48 bg-white dark:bg-background-card border border-gray-200 dark:border-border-color rounded-md shadow-lg p-2 text-xs text-gray-600 dark:text-text-secondary z-[60] font-sans whitespace-normal break-words">
              20-Day SMA crossed below 50-Day SMA in the past 5 days
            </div>
          </span>
        );
      }
    } else if (indicatorKey === 'SMA_50') {
      if (hasGoldenCross(accessors, 'SMA_50', 'SMA_200')) {
        goldenCrossIcon = (
          <span className="ml-1 group/golden-cross relative">
            <GoldenCrossIcon className="w-4 h-4" />
            <div className="absolute hidden group-hover/golden-cross:block left-0 top-full mt-1 w-48 bg-white dark:bg-background-card border border-gray-200 dark:border-border-color rounded-md shadow-lg p-2 text-xs text-gray-600 dark:text-text-secondary z-[60] font-sans whitespace-normal break-words">
              50-Day SMA crossed over 200-Day SMA in the past 5 days
            </div>
          </span>
        );
      } else if (hasDeathCross(accessors, 'SMA_50', 'SMA_200')) {
        deathCrossIcon = (
          <span className="ml-1 group/death-cross relative">
            <DeathCrossIcon className="w-4 h-4" />
            <div className="absolute hidden group-hover/death-cross:block left-0 top-full mt-1 w-48 bg-white dark:bg-background-card border border-gray-200 dark:border-border-color rounded-md shadow-lg p-2 text-xs text-gray-600 dark:text-text-secondary z-[60] font-sans whitespace-normal break-words">
              50-Day SMA crossed below 200-Day SMA in the past 5 days
            </div>
          </span>
        );
      }
    } else if (indicatorKey === 'SMA_W50') {
      if (hasGoldenCross(accessors, 'SMA_W50', 'SMA_W200')) {
        goldenCrossIcon = (
          <span className="ml-1 group/golden-cross relative">
            <GoldenCrossIcon className="w-4 h-4" />
            <div className="absolute hidden group-hover/golden-cross:block left-0 top-full mt-1 w-48 bg-white dark:bg-background-card border border-gray-200 dark:border-border-color rounded-md shadow-lg p-2 text-xs text-gray-600 dark:text-text-secondary z-[60] font-sans whitespace-normal break-words">
              50-Week SMA crossed over 200-Week SMA in the past 5 days
            </div>
          </span>
        );
      } else if (hasDeathCross(accessors, 'SMA_W50', 'SMA_W200')) {
        deathCrossIcon = (
          <span className="ml-1 group/death-cross relative">
            <DeathCrossIcon className="w-4 h-4" />
            <div className="absolute hidden group-hover/death-cross:block left-0 top-full mt-1 w-48 bg-white dark:bg-background-card border border-gray-200 dark:border-border-color rounded-md shadow-lg p-2 text-xs text-gray-600 dark:text-text-secondary z-[60] font-sans whitespace-normal break-words">
              50-Week SMA crossed below 200-Week SMA in the past 5 days
            </div>
          </span>
        );
      }
    } else if (indicatorKey === 'EMA_21') {
      if (hasGoldenCross(accessors, 'EMA_21', 'EMA_50')) {
        goldenCrossIcon = (
          <span className="ml-1 group/golden-cross relative">
            <GoldenCrossIcon className="w-4 h-4" />
            <div className="absolute hidden group-hover/golden-cross:block left-0 top-full mt-1 w-48 bg-white dark:bg-background-card border border-gray-200 dark:border-border-color rounded-md shadow-lg p-2 text-xs text-gray-600 dark:text-text-secondary z-[60] font-sans whitespace-normal break-words">
              21-Day EMA crossed over 50-Day EMA in the past 5 days
            </div>
          </span>
        );
      } else if (hasDeathCross(accessors, 'EMA_21', 'EMA_50')) {
        deathCrossIcon = (
          <span className="ml-1 group/death-cross relative">
            <DeathCrossIcon className="w-4 h-4" />
            <div className="absolute hidden group-hover/death-cross:block left-0 top-full mt-1 w-48 bg-white dark:bg-background-card border border-gray-200 dark:border-border-color rounded-md shadow-lg p-2 text-xs text-gray-600 dark:text-text-secondary z-[60] font-sans whitespace-normal break-words">
              21-Day EMA crossed below 50-Day EMA in the past 5 days
            </div>
          </span>
        );
      }
    } else if (indicatorKey === 'EMA_50') {
      if (hasGoldenCross(accessors, 'EMA_50', 'EMA_200')) {
        goldenCrossIcon = (
          <span className="ml-1 group/golden-cross relative">
            <GoldenCrossIcon className="w-4 h-4" />
            <div className="absolute hidden group-hover/golden-cross:block left-0 top-full mt-1 w-48 bg-white dark:bg-background-card border border-gray-200 dark:border-border-color rounded-md shadow-lg p-2 text-xs text-gray-600 dark:text-text-secondary z-[60] font-sans whitespace-normal break-words">
              50-Day EMA crossed over 200-Day EMA in the past 5 days
            </div>
          </span>
        );
      } else if (hasDeathCross(accessors, 'EMA_50', 'EMA_200')) {
        deathCrossIcon = (
          <span className="ml-1 group/death-cross relative">
            <DeathCrossIcon className="w-4 h-4" />
            <div className="absolute hidden group-hover/death-cross:block left-0 top-full mt-1 w-48 bg-white dark:bg-background-card border border-gray-200 dark:border-border-color rounded-md shadow-lg p-2 text-xs text-gray-600 dark:text-text-secondary z-[60] font-sans whitespace-normal break-words">
              50-Day EMA crossed below 200-Day EMA in the past 5 days
            </div>
          </span>
        );
      }
    }
  }
  
  return { goldenCross: goldenCrossIcon, deathCross: deathCrossIcon };
};

/**
 * Format statistic value for technicals (works with API response format)
 */
export const formatTechnicalsStatisticValue = (
  accessors: IndicatorAccessors,
  dailyPrices: { [daysAgo: number]: DailyPriceData },
  tickerData: TickerTechnicalDataFromAPI | null,
  tickerSymbol: string,
  indicatorKey: string,
  daysAgo: number,
  isLoading: boolean = false
): React.ReactNode => {
  // Show pulsing animation when loading
  if (isLoading) {
    return <div className="animate-pulse bg-gray-200 dark:bg-border-color h-4 w-16 rounded"></div>;
  }
  
  if (!tickerData) {
    return <span className="text-gray-400 dark:text-text-secondary">-</span>;
  }
  
  try {
    const value = accessors.getIndicatorValue(indicatorKey, daysAgo);
    const nonNumericValue = accessors.getIndicatorNonNumericValue(indicatorKey, daysAgo);
    
    // Use non-numeric value if available
    if (nonNumericValue !== null) {
      return <span className="text-text-light-neutral dark:text-text-secondary">{nonNumericValue}</span>;
    }
    
    // Get the appropriate color for this indicator and daysAgo
    const valueColor = getTechnicalsValueColor(accessors, dailyPrices, indicatorKey, daysAgo);
    
    // Handle special cases that don't come from technical indicators FIRST
    if (indicatorKey === 'last_closing_price') {
      const dailyPrice = dailyPrices[daysAgo];
      if (dailyPrice) {
        return <span className={valueColor}>${formatNumber(dailyPrice.close_price_adjusted)}</span>;
      }
      return <span className="text-gray-400 dark:text-text-secondary">-</span>;
    }
    
    if (indicatorKey === 'pct_off_52w_high') {
      const high52w = accessors.getIndicatorValue('52W_HIGH', daysAgo);
      const dailyPriceForHigh = dailyPrices[daysAgo];
      if (high52w && dailyPriceForHigh && high52w > 0) {
        const percentage = ((high52w - dailyPriceForHigh.close_price_adjusted) / high52w) * 100;
        return <span className={valueColor}>{formatNumber(percentage)}%</span>;
      }
      return <span className="text-gray-400 dark:text-text-secondary">-</span>;
    }
    
    if (indicatorKey === 'pct_above_52w_low') {
      const low52w = accessors.getIndicatorValue('52W_LOW', daysAgo);
      const dailyPriceForLow = dailyPrices[daysAgo];
      if (low52w && dailyPriceForLow && low52w > 0) {
        const percentage = ((dailyPriceForLow.close_price_adjusted - low52w) / low52w) * 100;
        return <span className={valueColor}>{formatNumber(percentage)}%</span>;
      }
      return <span className="text-gray-400 dark:text-text-secondary">-</span>;
    }
    
    // Handle technical indicators that don't have numeric values FIRST
    if (indicatorKey === 'FIB_SWING_PERIOD') {
      const startDate = accessors.getIndicatorNonNumericValue('FIB_SWING_START_DATE', daysAgo);
      const endDate = accessors.getIndicatorNonNumericValue('FIB_SWING_END_DATE', daysAgo);
      
      if (startDate && endDate) {
        const formatDate = (dateStr: string) => {
          try {
            const date = new Date(dateStr);
            if (isNaN(date.getTime())) {
              return dateStr;
            }
            return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
          } catch {
            return dateStr;
          }
        };
        return (
          <span className="text-text-light-neutral dark:text-text-secondary block leading-tight">
            <span className="block">{formatDate(startDate)}</span>
            <span className="block">{formatDate(endDate)}</span>
          </span>
        );
      }
      return <span className="text-gray-400 dark:text-text-secondary">-</span>;
    }
    
    if (indicatorKey === 'FIB_SWING_PERCENTAGE') {
      const swingPercentage = value;
      if (swingPercentage !== undefined && swingPercentage !== null) {
        const valueColor = getTechnicalsValueColor(accessors, dailyPrices, indicatorKey, daysAgo);
        const sign = swingPercentage >= 0 ? '+' : '-';
        const absValue = Math.abs(swingPercentage);
        return <span className={valueColor}>{sign}{formatNumber(absValue)}%</span>;
      }
      return <span className="text-gray-400 dark:text-text-secondary">-</span>;
    }
    
    if (value === null || value === undefined) {
      return <span className="text-gray-400 dark:text-text-secondary">-</span>;
    }
    
    // Continue with default formatting for numeric indicators
    // Handle VMA values first - round to nearest integer and format with commas
    if (indicatorKey.startsWith('VMA_')) {
      const roundedValue = Math.round(value);
      return <span className={valueColor}>{formatNumber(roundedValue, 0)}</span>;
    } else if (indicatorKey.includes('RSI')) {
      // RSI values: 2 decimal places with color coding and fire/ice icons
      const rsiValue = value;
      
      let icon = null;
      if (rsiValue > 70) {
        icon = (
          <span className="ml-1 text-red-500" title="Overbought">
            🔥
          </span>
        );
      } else if (rsiValue < 30) {
        icon = (
          <span className="ml-1 text-blue-500" title="Oversold">
            ❄️
          </span>
        );
      }
      
      return (
        <span className={valueColor}>
          {formatNumber(value)}
          {icon}
        </span>
      );
    } else if (indicatorKey.includes('%')) {
      // Other percentage values: 2 decimal places
      return <span className="text-text-secondary">{formatNumber(value)}</span>;
    } else if (indicatorKey.includes('MACD')) {
      // MACD values: 2 decimal places with color coding
      return <span className={valueColor}>{formatNumber(value)}</span>;
    } else if (indicatorKey.startsWith('STOCH_')) {
      // Stochastic Oscillator values: 2 decimal places with color coding and fire/snowflake icons
      const stochValue = value;
      
      let icon = null;
      if (stochValue > 80) {
        icon = (
          <span className="ml-1 text-red-500" title="Overbought">
            🔥
          </span>
        );
      } else if (stochValue < 20) {
        icon = (
          <span className="ml-1 text-blue-500" title="Oversold">
            ❄️
          </span>
        );
      }
      
      return (
        <span className={valueColor}>
          {formatNumber(value)}
          {icon}
        </span>
      );
    } else if (indicatorKey.startsWith('FIB_') && indicatorKey !== 'FIB_SWING_PERIOD' && indicatorKey !== 'FIB_SWING_PERCENTAGE') {
      // Fibonacci level values: format as price with $ and icons
      const tickerDataForIcons = buildTickerTechnicalDataForIcons(tickerData, dailyPrices, tickerSymbol, daysAgo);
      const fibonacciIcon = tickerDataForIcons ? getFibonacciIcon(tickerDataForIcons, indicatorKey) : null;
      return (
        <span className={`${valueColor} inline-flex items-center whitespace-nowrap`}>
          ${formatNumber(value)}
          {fibonacciIcon}
        </span>
      );
    } else if (indicatorKey.startsWith('BB_')) {
      // Bollinger Bands: format as currency with icons and color
      const tickerDataForIcons = buildTickerTechnicalDataForIcons(tickerData, dailyPrices, tickerSymbol, daysAgo);
      const bbIcon = tickerDataForIcons ? getBollingerBandIcon(tickerDataForIcons, indicatorKey) : null;
      return (
        <span className={`${valueColor} inline-flex items-center whitespace-nowrap`}>
          ${formatNumber(value)}
          {bbIcon}
        </span>
      );
    } else if (indicatorKey.includes('SMA') || indicatorKey.includes('EMA') || indicatorKey === '52W_HIGH' || indicatorKey === '52W_LOW') {
      // Price-based indicators: format as currency with commas and cross icons
      const crossIcons = getCrossIcon(accessors, indicatorKey, daysAgo);
      return (
        <span className={`${valueColor} inline-flex items-center whitespace-nowrap`}>
          ${formatNumber(value)}
          {crossIcons.goldenCross}
          {crossIcons.deathCross}
        </span>
      );
    } else {
      // Default: 2 decimal places with commas
      return <span className={valueColor}>{formatNumber(value)}</span>;
    }
  } catch (error) {
    console.error(`Error in formatTechnicalsStatisticValue for ${indicatorKey}, daysAgo ${daysAgo}:`, error);
    return <span className="text-red-500">ERROR</span>;
  }
};

